/**
 * Created by Yehoshua Leventhal  on 23/03/2023.
 */

import type { FC, ReactNode } from "react";
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import type { ITitleOffsets, Positions, Titles } from "./titlesPositionerTypes";
import { TitlePositioner, TitleText } from "./styledComponents";
import layoutConfiguration from "app/components/layout/layoutConfiguration";

const HORIZONTAL_PADDING = 16;
const VERTICAL_PADDING = 8;
const HIGHCHARTS_BACKGROUND_IMAGE_CONTAINER = ".highcharts-container image";
interface ITitlesPositionerProps {
    titles: Titles;
    horizontalPadding?: number;
    verticalPadding?: number;
    highChartContainerSelector?: string;
}

export const TitlesPositioner: FC<ITitlesPositionerProps> = (props) => {
    const {
        titles,
        verticalPadding = VERTICAL_PADDING,
        horizontalPadding = HORIZONTAL_PADDING,
        highChartContainerSelector = HIGHCHARTS_BACKGROUND_IMAGE_CONTAINER,
    } = props;
    const TitlesContainerRef = useRef(null);

    const [TitleOffsets, setTitleOffsets] = useState<ITitleOffsets>({
        topLeft: { top: 0, left: 0 },
        topRight: { top: 0, right: 0 },
        bottomLeft: { bottom: 0, left: 0 },
        bottomRight: { bottom: 0, right: 0 },
    });
    const position = () => {
        const imageContainer = document.querySelector(highChartContainerSelector);
        if (TitlesContainerRef.current && imageContainer) {
            const TitlesContainerPositions: DOMRect = TitlesContainerRef.current.offsetParent.getBoundingClientRect();
            const imageContainerPositions = imageContainer.getBoundingClientRect();

            const left = imageContainerPositions.left - TitlesContainerPositions.left + horizontalPadding;
            const right = TitlesContainerPositions.right - imageContainerPositions.right + horizontalPadding;
            const top = imageContainerPositions.top - TitlesContainerPositions.top + verticalPadding;
            const bottom = TitlesContainerPositions.bottom - imageContainerPositions.bottom + verticalPadding;

            const TitleOffsets = {
                topLeft: { top, left },
                topRight: { top, right },
                bottomLeft: { bottom, left },
                bottomRight: { bottom, right },
            };
            setTitleOffsets(TitleOffsets);
        }
    };
    useLayoutEffect(position, [titles]);

    useEffect(() => {
        const onWindowResize = () => {
            document.querySelector(`.${layoutConfiguration.pageScroller}`)?.scroll(0, 0);
            position();
        };
        window.addEventListener("resize", onWindowResize);
        return () => {
            window.removeEventListener("resize", onWindowResize);
        };
    }, []);

    const TitlesComponents = useMemo(() => {
        if (!titles) {
            return null;
        }
        return Object.entries(titles).reduce((TitlesComponents, [position, title]: [Positions, ReactNode]) => {
            if (typeof title === "string") {
                return {
                    ...TitlesComponents,
                    [position]: <TitleText>{title}</TitleText>,
                };
            }
            return {
                ...TitlesComponents,
                [position]: title,
            };
        }, {} as Titles);
    }, [titles]);

    return (
        <div ref={TitlesContainerRef}>
            {Object.keys(TitlesComponents).map((position) => (
                <TitlePositioner
                    key={position}
                    positions={{
                        top: TitleOffsets[position].top,
                        bottom: TitleOffsets[position].bottom,
                        left: TitleOffsets[position].left,
                        right: TitleOffsets[position].right,
                    }}
                >
                    {TitlesComponents?.[position]}
                </TitlePositioner>
            ))}
        </div>
    );
};
